import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Product } from '../../types';
import { useAuth } from '../../components/AuthProvider';
import { 
  getTournamentDetails, 
  getTournamentEntries, 
  getUserTournamentEntry, 
  joinTournamentWithTicket, 
  updateEntryStatus,
  removeTournamentEntry,
  listUserDecks,
  submitOrUpdateTournamentDeck,
  checkUserHasProductOrder
} from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';

interface EventDetailsProps {
  onAddToCart?: (product: Product) => void;
}

export const EventDetails: React.FC<EventDetailsProps> = ({ onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [event, setEvent] = useState<any>(null);
  const [entries, setEntries] = useState<any[]>([]);
  const [userEntry, setUserEntry] = useState<any>(null);
  const [existingTicketOrder, setExistingTicketOrder] = useState<any>(null);
  const [userDecks, setUserDecks] = useState<any[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string>('');
  const [ticketInput, setTicketInput] = useState<string>('');
  const [joining, setJoining] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeckModal, setShowDeckModal] = useState(false);
  const [selectedDeckForModal, setSelectedDeckForModal] = useState<string>('');
  const [savingDeck, setSavingDeck] = useState(false);

  const isLojista = user?.role_id === 6;

  const fetchEventData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getTournamentDetails(id);
      if (data) {
        setEvent(data);
        // Load entries
        const entriesData = await getTournamentEntries(id);
        setEntries(entriesData);

        // Load current user's registration
        if (user) {
          const myEntry = await getUserTournamentEntry(id);
          setUserEntry(myEntry);
          if (myEntry?.tournament_deck_id || myEntry?.tournament_deck?.source_deck_id) {
            setSelectedDeckForModal(myEntry.tournament_deck?.source_deck_id || myEntry.tournament_deck_id || '');
          }

          // Check if current user already has an active order for any ticket product of this tournament
          const ticketProdIds = (data.tickets || []).map((t: any) => t.product_id || t.product?.id).filter(Boolean);
          if (ticketProdIds.length > 0) {
            let foundOrder = null;
            for (const pid of ticketProdIds) {
              const check = await checkUserHasProductOrder(pid);
              if (check.hasOrder) {
                foundOrder = check.order || { id: 'ativo', product_id: pid };
                break;
              }
            }
            setExistingTicketOrder(foundOrder);
          } else {
            setExistingTicketOrder(null);
          }

          // Load user's decks for this game
          if (data.cardgame_id) {
            const decks = await listUserDecks(String(data.cardgame_id));
            setUserDecks(decks);
            if (!selectedDeckId && decks.length > 0) {
              setSelectedDeckId(decks[0].id);
            }
            if (!selectedDeckForModal && decks.length > 0) {
              setSelectedDeckForModal(decks[0].id);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error loading tournament details:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventData();
  }, [id, user]);

  const displayedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      if (a.final_position && b.final_position) {
        return Number(a.final_position) - Number(b.final_position);
      }
      if (a.final_position) return -1;
      if (b.final_position) return 1;
      return (b.points || 0) - (a.points || 0);
    });
  }, [entries]);

  const handleJoinWithTicket = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!user) {
      showNotification("Por favor, faça login para ingressar no torneio.", "error");
      navigate('/login');
      return;
    }

    const cleanCode = ticketInput.trim().toUpperCase();
    if (!cleanCode) {
      showNotification("Por favor, digite o código do ticket.", "error");
      return;
    }

    if (event?.decklist_required && !selectedDeckId) {
      showNotification("Este torneio exige o envio prévio de uma decklist.", "error");
      return;
    }

    setJoining(true);
    try {
      const res = await joinTournamentWithTicket(cleanCode, {
        tournamentId: event?.id,
        deckId: selectedDeckId || null
      });

      if (res.success) {
        showNotification("Inscrição realizada com sucesso! Você está participando do torneio.", "success");
        setTicketInput('');
        await fetchEventData();
      } else {
        showNotification(res.error || "Código de ticket incorreto ou erro ao ingressar.", "error");
      }
    } catch (err: any) {
      showNotification(err?.message || "Erro ao tentar ingressar no torneio.", "error");
    } finally {
      setJoining(false);
    }
  };

  const handleSaveDecklist = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!user) {
      showNotification("Por favor, faça login.", "error");
      return;
    }

    if (!selectedDeckForModal) {
      showNotification("Selecione um deck para enviar.", "error");
      return;
    }

    const currentTournamentId = event?.id || id;
    if (!currentTournamentId) {
      showNotification("Identificador do torneio não encontrado.", "error");
      return;
    }

    if (event?.status === 'running' || event?.status === 'finished') {
      showNotification("Não é possível alterar a decklist após o início do torneio.", "error");
      return;
    }

    setSavingDeck(true);
    try {
      const res = await submitOrUpdateTournamentDeck(currentTournamentId, selectedDeckForModal);
      if (res.success) {
        showNotification("Decklist enviada e registrada com sucesso para o torneio!", "success");
        setShowDeckModal(false);
        await fetchEventData();
      } else {
        showNotification(res.error || "Erro ao registrar decklist.", "error");
      }
    } catch (err: any) {
      showNotification(err?.message || "Erro ao registrar decklist.", "error");
    } finally {
      setSavingDeck(false);
    }
  };

  const handleOrganizerStatusChange = async (entryId: number, newStatus: string) => {
    try {
      const res = await updateEntryStatus(entryId, newStatus);
      if (res && (res as any).error) {
        showNotification((res as any).error, "error");
      } else {
        const label = newStatus === 'approved' ? 'Confirmado' : newStatus === 'rejected' ? 'Recusado' : 'Pendente';
        showNotification(`Status do participante atualizado para ${label}.`, "success");
        setEntries(prev => prev.map(e => e.id === entryId ? { ...e, status: newStatus } : e));
        if (userEntry && userEntry.id === entryId) {
          setUserEntry((prev: any) => ({ ...prev, status: newStatus }));
        }
      }
    } catch (err: any) {
      showNotification(err?.message || "Erro ao atualizar status.", "error");
    }
  };

  const handleOrganizerRemoveEntry = async (entryId: number, participantName: string) => {
    if (!window.confirm(`Deseja realmente cancelar a inscrição de "${participantName}" deste torneio?`)) {
      return;
    }
    try {
      const res = await removeTournamentEntry(entryId);
      if (res && (res as any).error) {
        showNotification((res as any).error, "error");
      } else {
        showNotification(`Inscrição de "${participantName}" cancelada com sucesso.`, "success");
        setEntries(prev => prev.filter(e => e.id !== entryId));
        if (userEntry && userEntry.id === entryId) {
          setUserEntry(null);
        }
      }
    } catch (err: any) {
      showNotification(err?.message || "Erro ao cancelar inscrição.", "error");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
    </div>
  );

  if (!event) return (
    <div className="text-center py-20">
      <h2 className="text-2xl font-bold text-white">Torneio não encontrado</h2>
      <Link to="/torneios" className="text-purple-400 mt-4 block">Voltar para torneios</Link>
    </div>
  );

  const mainTicket = event.tickets?.[0];
  const isCancelled = event.status === 'cancelled' || event.status === 'canceled' || event.status === 'cancelado';
  const isOpenStatus = event.status === 'open' || event.status === 'aberto';
  const isSoldOut = (event.max_players && entries.length >= event.max_players) || (mainTicket && mainTicket.sold_quantity >= mainTicket.max_quantity);
  const saleEnded = mainTicket && new Date() > new Date(mainTicket.sale_end);
  const saleNotStarted = mainTicket && new Date() < new Date(mainTicket.sale_start);
  const isFinished = event.status === 'finished' || event.status === 'finalizado';
  const hasOrderedTicket = !!(existingTicketOrder || userEntry);

  const hostStore = event.store || event.creator?.store_members?.[0]?.store;
  const isOrganizer = !!(user && event && (
    user.id === event.created_by ||
    user.role_id === 1 ||
    (hostStore && (user.store_id === hostStore.id || (user as any).store_members?.some((sm: any) => sm.store_id === hostStore.id)))
  ));

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Top Breadcrumb */}
      <div className="flex items-center justify-between text-xs">
        <Link to="/torneios" className="text-slate-400 hover:text-purple-400 transition-colors flex items-center space-x-1.5 font-bold">
          <i className="fas fa-arrow-left"></i>
          <span>Voltar para todos os torneios</span>
        </Link>
        <div className="flex items-center space-x-3">
          {isOrganizer && (
            <Link
              to={`/lojista/torneios/${event.id}`}
              className="bg-purple-950/60 hover:bg-purple-900/80 border border-purple-500/30 text-purple-300 hover:text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-colors flex items-center space-x-1.5"
            >
              <i className="fas fa-sliders text-purple-400"></i>
              <span>Painel do Organizador</span>
            </Link>
          )}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              showNotification("Link do torneio copiado!", "success");
            }}
            className="bg-slate-900 border border-slate-800 hover:border-purple-500/40 text-slate-300 text-xs px-3 py-1.5 rounded-xl transition-colors flex items-center space-x-1.5"
          >
            <i className="fas fa-share-nodes text-purple-400"></i>
            <span>Compartilhar</span>
          </button>
        </div>
      </div>

      {/* Cancellation Banner */}
      {isCancelled && (
        <div className="bg-rose-950/30 border border-rose-900/50 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400 text-2xl shrink-0">
              <i className="fas fa-ban"></i>
            </div>
            <div>
              <h3 className="text-lg font-black text-white uppercase tracking-tight">Torneio Cancelado</h3>
              <p className="text-xs text-rose-300/80 mt-0.5">
                Este torneio foi cancelado pela loja/organizador sem declaração de vencedores. As inscrições foram encerradas e nenhuma rodada será disputada.
              </p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-rose-500/20 text-rose-300 border border-rose-500/30 shrink-0">
            Cancelado
          </span>
        </div>
      )}

      {/* Hero Banner */}
      <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900">
        {event.image_url ? (
          <div className="relative h-48 md:h-64 overflow-hidden bg-slate-950">
            <img 
              src={event.image_url} 
              className="w-full h-full object-cover" 
              alt={event.name} 
              loading="eager"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-black/20"></div>
          </div>
        ) : null}
        
        <div className="p-6 md:p-8 space-y-4">
          <div className="flex flex-wrap gap-2.5">
            <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest ${
              isCancelled
                ? 'bg-rose-600 text-white'
                : event.status === 'open' 
                  ? 'bg-emerald-600 text-white' 
                  : event.status === 'scheduled' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-700 text-slate-300'
            }`}>
              {isCancelled ? 'Cancelado' : event.status === 'open' ? 'Inscrições Abertas' : event.status === 'scheduled' ? 'Agendado' : 'Finalizado'}
            </span>
            <span className="bg-slate-800 text-slate-300 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-slate-700">
              {event.games?.name}
            </span>
            {(event.game_formats?.name || event.tournament_formats?.name) && (
              <span className="bg-purple-950/80 text-purple-300 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-purple-500/30">
                {event.game_formats?.name || event.tournament_formats?.name}
              </span>
            )}
            {event.online ? (
              <span className="bg-cyan-950/80 text-cyan-300 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-cyan-500/30">
                <i className="fas fa-globe mr-1"></i> Online
              </span>
            ) : (
              <span className="bg-amber-950/80 text-amber-300 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-amber-500/30">
                <i className="fas fa-location-dot mr-1"></i> Presencial
              </span>
            )}
          </div>

          <h1 className="text-2xl md:text-4xl font-black text-white leading-tight tracking-tight">{event.name}</h1>

          <div className="flex flex-wrap items-center gap-6 text-slate-300">
            <div className="flex items-center space-x-2">
              <i className="fas fa-calendar-alt text-purple-400"></i>
              <span className="text-sm font-bold">
                {new Date(event.start_date).toLocaleDateString('pt-BR', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <i className="fas fa-location-dot text-purple-400"></i>
              <span className="text-sm font-bold">
                {hostStore ? (
                  <Link 
                    to={`/loja/${hostStore.slug || hostStore.id}`}
                    className="hover:text-purple-400 hover:underline transition-colors"
                  >
                    {hostStore.name}
                  </Link>
                ) : (
                  event.location || 'Local da Loja'
                )}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <i className="fas fa-users text-purple-400"></i>
              <span className="text-sm font-bold">
                {entries.length} / {event.max_players || '∞'} Inscritos
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* User Enrollment Status & Decklist Banner */}
      {userEntry && (
        <div className="space-y-4">
          <div 
            className="p-6 rounded-2xl bg-slate-900 border border-emerald-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 text-xl shrink-0">
                <i className="fas fa-check-circle"></i>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-white font-black text-base">Você está inscrito neste torneio!</h3>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full ${
                    userEntry.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                  }`}>
                    {userEntry.status === 'approved' ? 'Inscrição Aprovada' : 'Inscrição Pendente'}
                  </span>
                </div>
                <p className="text-slate-400 text-xs mt-0.5">
                  Pontuação atual: <strong className="text-white font-mono">{userEntry.points || 0} pts</strong> • Registrado em {new Date(userEntry.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
              <div className="flex items-center space-x-2 text-xs text-slate-400 bg-slate-950 px-3.5 py-2 rounded-xl border border-slate-800">
                <i className="fas fa-lock text-slate-500 text-[11px]"></i>
                <span className="text-[11px] font-medium text-slate-400">Cancelamento ou alteração sob gestão do organizador</span>
              </div>
              {isOrganizer && (
                <Link
                  to={`/lojista/torneios/${event.id}`}
                  className="text-xs font-bold text-purple-300 hover:text-white bg-purple-900/40 hover:bg-purple-800/60 border border-purple-500/30 px-3.5 py-2 rounded-xl transition-colors flex items-center space-x-1.5"
                >
                  <i className="fas fa-sliders text-purple-400"></i>
                  <span>Painel do Organizador</span>
                </Link>
              )}
            </div>
          </div>

          {/* Decklist Management Box */}
          <div className="p-6 rounded-2xl bg-slate-900 border border-purple-500/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start md:items-center space-x-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center text-purple-400 text-xl shrink-0">
                <i className="fas fa-layer-group"></i>
              </div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Sua Decklist para o Torneio</span>
                  {userEntry.tournament_deck ? (
                    <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
                      Deck Registrado
                    </span>
                  ) : (
                    <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
                      Decklist Pendente
                    </span>
                  )}
                </div>
                
                {userEntry.tournament_deck ? (
                  <div>
                    <h4 className="text-emerald-400 font-bold text-sm flex items-center space-x-1.5">
                      <i className="fas fa-check-circle text-emerald-400"></i>
                      <span>Deck enviado</span>
                    </h4>
                    <p className="text-slate-400 text-xs">
                      Enviado em {new Date(userEntry.tournament_deck.created_at || userEntry.updated_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">
                    Você ainda não vinculou uma decklist para este evento. Envie seu deck a qualquer momento antes do início das rodadas.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-3 w-full md:w-auto justify-end">
              {event.status === 'running' || event.status === 'finished' ? (
                <span className="text-[11px] font-bold text-slate-500 bg-slate-800 px-4 py-2 rounded-xl flex items-center space-x-1.5 border border-slate-700">
                  <i className="fas fa-lock text-slate-400"></i>
                  <span>Decklist Bloqueada (Torneio Iniciado)</span>
                </span>
              ) : (
                <button
                  onClick={() => setShowDeckModal(true)}
                  className="text-xs font-bold text-white bg-purple-600 hover:bg-purple-500 px-4 py-2.5 rounded-xl transition-colors flex items-center space-x-1.5"
                >
                  <i className="fas fa-arrow-up-from-bracket"></i>
                  <span>{userEntry.tournament_deck ? 'Alterar Decklist' : 'Enviar Decklist'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Grid: Details + Join Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column: Information & Participants */}
        <div className="lg:col-span-2 space-y-8">
          {/* About Event */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-6">
            <h2 className="text-2xl font-black text-white flex items-center space-x-2">
              <i className="fas fa-circle-info text-purple-400 text-xl"></i>
              <span>Sobre o Torneio</span>
            </h2>
            <p className="text-slate-300 leading-relaxed whitespace-pre-line text-sm">
              {event.description || 'Nenhuma descrição detalhada fornecida para este evento.'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-800">
              <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl space-y-1">
                <div className="flex items-center space-x-2 text-yellow-500 mb-1">
                  <i className="fas fa-award text-base"></i>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Formato</span>
                </div>
                <h4 className="font-bold text-white text-sm">{event.game_formats?.name || event.tournament_formats?.name || 'Padrão'}</h4>
                <p className="text-[11px] text-slate-500">{event.tournament_types?.name || 'Suíço'}</p>
              </div>

              <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl space-y-1">
                <div className="flex items-center space-x-2 text-blue-400 mb-1">
                  <i className="fas fa-users text-base"></i>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Capacidade</span>
                </div>
                <h4 className="font-bold text-white text-sm">{entries.length} de {event.max_players || '∞'}</h4>
                <p className="text-[11px] text-slate-500">{event.max_players ? `${event.max_players - entries.length} vagas restantes` : 'Sem limite estrito'}</p>
              </div>

              <div className="bg-slate-950 border border-slate-800 p-4 rounded-xl space-y-1">
                <div className="flex items-center space-x-2 text-purple-400 mb-1">
                  <i className="fas fa-layer-group text-base"></i>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Decklist</span>
                </div>
                <h4 className="font-bold text-white text-sm">{event.decklist_required ? 'Obrigatória' : 'Opcional'}</h4>
                <p className="text-[11px] text-slate-500">Formato {event.games?.name}</p>
              </div>
            </div>
          </div>

          {/* Participants List */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-800 bg-slate-900 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                  <i className="fas fa-list-ol"></i>
                </div>
                <div>
                  <h3 className="font-black text-white text-base">Participantes Confirmados</h3>
                  <p className="text-xs text-slate-400">{entries.length} jogadores inscritos</p>
                </div>
              </div>
              <span className="text-xs font-mono font-bold bg-slate-800 px-3 py-1 rounded-full text-slate-300">
                {entries.length}/{event.max_players || '∞'}
              </span>
            </div>

            {entries.length === 0 ? (
              <div className="p-12 text-center space-y-3">
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-slate-600 mx-auto text-xl">
                  <i className="fas fa-user-plus"></i>
                </div>
                <h4 className="text-white font-bold text-sm">Nenhum jogador inscrito ainda</h4>
                <p className="text-slate-500 text-xs max-w-sm mx-auto">
                  Seja o primeiro a ingressar informando o código do ticket ou adquirindo sua inscrição!
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-800/60">
                {displayedEntries.map((entry, index) => (
                  <div key={entry.id || index} className="p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-center space-x-3.5">
                      <span className="text-xs font-mono font-bold text-slate-600 w-5 text-right">{index + 1}.</span>
                      <div className="w-10 h-10 rounded-full bg-slate-800 border border-white/10 overflow-hidden shrink-0">
                        {entry.user?.avatar ? (
                          <img src={entry.user.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400 font-black text-sm">
                            {entry.user?.fullname?.charAt(0) || entry.user?.username?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-bold text-white text-sm flex items-center space-x-2 flex-wrap gap-y-1">
                          <span>{entry.user?.fullname || entry.user?.username || `Jogador #${entry.id}`}</span>
                          {entry.final_position !== null && entry.final_position !== undefined && Number(entry.final_position) >= 1 && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black font-mono border ${
                              Number(entry.final_position) === 1
                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                : Number(entry.final_position) === 2
                                ? 'bg-slate-300/20 text-slate-200 border-slate-300/40'
                                : Number(entry.final_position) === 3
                                ? 'bg-amber-700/20 text-amber-500 border-amber-700/40'
                                : 'bg-purple-950/40 text-purple-300 border-purple-500/30'
                            }`}>
                              {Number(entry.final_position) === 1 ? '🥇 1º' : Number(entry.final_position) === 2 ? '🥈 2º' : Number(entry.final_position) === 3 ? '🥉 3º' : `${entry.final_position}º`}
                            </span>
                          )}
                          {entry.user_id === user?.id && (
                            <span className="text-[9px] bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full font-bold border border-purple-500/30">
                              Você
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono">@{entry.user?.username || 'user'}</div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {entry.tournament_deck ? (
                        <span className="text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1 rounded-lg flex items-center space-x-1.5">
                          <i className="fas fa-check-circle text-emerald-400 text-[10px]"></i>
                          <span>Deck enviado</span>
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-500 italic bg-slate-950/60 px-2 py-1 rounded-lg border border-slate-800/80">
                          Sem decklist
                        </span>
                      )}

                      {entry.points > 0 && (
                        <span className="text-xs font-mono font-black text-purple-400 bg-purple-950/40 px-2.5 py-1 rounded-lg border border-purple-500/20">
                          {entry.points} pts
                        </span>
                      )}
                      {isOrganizer && !isFinished ? (
                        <div className="flex items-center space-x-1.5 pl-2 border-l border-slate-800">
                          <select
                            value={entry.status}
                            onChange={(e) => handleOrganizerStatusChange(entry.id, e.target.value)}
                            className={`text-[10px] font-bold rounded-lg px-2 py-1 outline-none border transition-all cursor-pointer ${
                              entry.status === 'approved'
                                ? 'bg-emerald-950/40 text-emerald-400 border-emerald-500/30'
                                : entry.status === 'rejected'
                                ? 'bg-red-950/40 text-red-400 border-red-500/30'
                                : 'bg-amber-950/40 text-amber-400 border-amber-500/30'
                            }`}
                            title="Alterar status do participante (Organizador)"
                          >
                            <option value="approved" className="bg-slate-900 text-emerald-400">Confirmado</option>
                            <option value="pending" className="bg-slate-900 text-amber-400">Pendente</option>
                            <option value="rejected" className="bg-slate-900 text-red-400">Recusado</option>
                          </select>
                          <button
                            onClick={() => handleOrganizerRemoveEntry(entry.id, entry.user?.fullname || entry.user?.username || 'Participante')}
                            className="text-slate-500 hover:text-rose-400 p-1.5 rounded-lg hover:bg-rose-500/10 border border-transparent hover:border-rose-500/20 transition-all"
                            title="Cancelar inscrição deste participante"
                          >
                            <i className="fas fa-trash-can text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ${
                          entry.status === 'approved' 
                            ? 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20' 
                            : entry.status === 'rejected'
                            ? 'text-red-400 bg-red-500/10 border border-red-500/20'
                            : 'text-amber-400 bg-amber-500/10 border border-amber-500/20'
                        }`}>
                          {entry.status === 'approved' ? 'Confirmado' : entry.status === 'rejected' ? 'Recusado' : 'Pendente'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column: Ticket Entry & Store Info */}
        <div className="space-y-6">
          {/* Ticket Purchase Card (só exibe se o status for open ou aberto) */}
          {mainTicket && isOpenStatus && (
            hasOrderedTicket ? (
              <div className="bg-slate-900 border border-purple-500/30 rounded-2xl p-6 md:p-8 space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0">
                    <i className="fas fa-ticket text-lg"></i>
                  </div>
                  <div>
                    <p className="text-[10px] text-purple-400 uppercase font-black tracking-widest">Ingresso Já Adquirido</p>
                    <p className="text-sm font-bold text-white">Você já possui este ingresso</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {userEntry 
                    ? "Sua vaga já está confirmada e vinculada ao torneio. Acompanhe sua participação e envie sua decklist acima."
                    : "Você já possui um pedido registrado para este ingresso. Não é necessário comprar novamente."}
                </p>
                <div className="flex flex-col gap-2 pt-1">
                  <Link 
                    to="/ingressos?tab=my-tickets"
                    className="inline-flex items-center justify-center w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 px-4 rounded-xl text-xs transition-colors space-x-2 uppercase tracking-wider"
                  >
                    <i className="fas fa-ticket text-xs"></i>
                    <span>Ver em Meus Ingressos</span>
                  </Link>
                  {existingTicketOrder?.id && (
                    <Link 
                      to={`/pedido/${existingTicketOrder.id}`}
                      className="inline-flex items-center justify-center w-full bg-slate-800 hover:bg-slate-700 text-purple-300 font-bold py-2.5 px-4 rounded-xl text-xs transition-colors space-x-2 border border-slate-700 uppercase tracking-wider"
                    >
                      <i className="fas fa-receipt text-purple-400"></i>
                      <span>Acompanhar Pedido #{existingTicketOrder.id}</span>
                    </Link>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-6">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-black tracking-widest mb-1">Ingresso Oficial</p>
                  <div className="flex items-end space-x-2">
                    <span className="text-3xl font-black text-emerald-400">
                      R$ {(mainTicket.product?.mspr || mainTicket.product?.msrp || 0).toFixed(2)}
                    </span>
                    <span className="text-slate-500 text-xs font-bold mb-1">/ vaga</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {!isLojista ? (
                    isSoldOut ? (
                      <Link 
                        to={`/ingressos?torneio=${event.id}`}
                        className="w-full bg-slate-800/80 border border-rose-500/30 text-rose-400 font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center space-x-2 text-xs uppercase tracking-widest hover:bg-slate-800"
                      >
                        <i className="fas fa-ban text-sm"></i>
                        <span>Ingressos Esgotados</span>
                      </Link>
                    ) : saleEnded ? (
                      <Link 
                        to={`/ingressos?torneio=${event.id}`}
                        className="w-full bg-slate-800/80 border border-slate-700 text-slate-400 font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center space-x-2 text-xs uppercase tracking-widest hover:bg-slate-800"
                      >
                        <i className="fas fa-clock text-sm"></i>
                        <span>Vendas Encerradas</span>
                      </Link>
                    ) : saleNotStarted ? (
                      <Link 
                        to={`/ingressos?torneio=${event.id}`}
                        className="w-full bg-slate-800/80 border border-amber-500/30 text-amber-300 font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center space-x-2 text-xs uppercase tracking-widest hover:bg-slate-800"
                      >
                        <i className="fas fa-calendar-clock text-sm"></i>
                        <span>Vendas em Breve</span>
                      </Link>
                    ) : (
                      <Link 
                        to={`/ingressos?torneio=${event.id}`}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center space-x-3 text-xs uppercase tracking-widest shadow-lg shadow-emerald-950/40"
                      >
                        <i className="fas fa-ticket text-sm"></i>
                        <span>Adquirir na Página de Ingressos</span>
                      </Link>
                    )
                  ) : (
                    <div className="bg-slate-800/50 border border-slate-700/50 p-4 rounded-xl text-center">
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                        Lojistas não podem adquirir ingressos.
                      </p>
                    </div>
                  )}

                  <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                    A aquisição do ingresso é realizada exclusivamente através da <Link to={`/ingressos?torneio=${event.id}`} className="text-purple-400 hover:underline font-bold">página de ingressos</Link>.
                  </p>
                </div>
              </div>
            )
          )}

          {/* Host & Organizer Info */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Organizador do Evento</p>
            <div className="flex items-center space-x-3">
              <img 
                src={event.creator?.avatar || "https://i.pravatar.cc/150?u=lojista"} 
                className="w-12 h-12 rounded-full border border-slate-700 object-cover" 
                alt="Organizador" 
              />
              <div>
                <p className="text-sm font-black text-white uppercase italic tracking-tight">
                  {event.creator?.fullname || event.creator?.username || 'Organizador'}
                </p>
                {hostStore ? (
                  <Link to={`/loja/${hostStore.slug || hostStore.id}`} className="text-xs text-purple-400 hover:underline block font-bold">
                    {hostStore.name}
                  </Link>
                ) : (
                  <p className="text-[11px] text-slate-500 font-bold uppercase">Lojista Parceiro</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Enviar ou Alterar Decklist */}
      <AnimatePresence>
        {showDeckModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
            <div
              className="bg-slate-900 border border-slate-800 w-full max-w-lg rounded-2xl p-6 md:p-8 space-y-6 relative"
            >
              <button
                onClick={() => setShowDeckModal(false)}
                className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-lg"></i>
              </button>

              <div className="space-y-1">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/30 text-[10px] font-black text-purple-300 uppercase tracking-widest">
                  <i className="fas fa-layer-group"></i> Registro de Decklist
                </div>
                <h3 className="text-xl font-black text-white">
                  {userEntry?.tournament_deck ? 'Alterar Decklist' : 'Enviar Decklist'}
                </h3>
                <p className="text-xs text-slate-400">
                  Você pode enviar ou alterar sua decklist a qualquer momento antes do início oficial do torneio.
                </p>
              </div>

              {userDecks.length === 0 ? (
                <div className="p-6 rounded-2xl bg-slate-950/60 border border-slate-800 text-center space-y-3">
                  <i className="fas fa-folder-open text-2xl text-slate-600"></i>
                  <h4 className="font-bold text-white text-sm">Nenhum deck criado para {event.games?.name}</h4>
                  <p className="text-xs text-slate-400">
                    Você ainda não possui nenhum deck salvo em sua conta para este card game.
                  </p>
                  <Link
                    to="/decks"
                    className="inline-block text-xs font-bold text-purple-400 hover:text-purple-300 hover:underline pt-1"
                  >
                    Criar um deck no Deckbuilder &rarr;
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSaveDecklist} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                      Selecione um dos seus Decks Salvos
                    </label>
                    <select
                      value={selectedDeckForModal}
                      onChange={(e) => setSelectedDeckForModal(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-purple-500 transition-colors cursor-pointer font-bold"
                    >
                      {userDecks.map(deck => (
                        <option key={deck.id} value={deck.id}>
                          {deck.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Summary of selected deck */}
                  {(() => {
                    const currentSelected = userDecks.find(d => String(d.id) === String(selectedDeckForModal));
                    if (!currentSelected) return null;
                    return (
                      <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-white">{currentSelected.name}</span>
                          <span className="text-purple-400 font-mono text-[11px] font-bold">Deck selecionado</span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          Ao confirmar, uma cópia segura (snapshot) desta decklist será vinculada ao torneio.
                        </p>
                      </div>
                    );
                  })()}

                  <div className="flex items-center space-x-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowDeckModal(false)}
                      className="w-1/2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-xl transition-colors text-xs uppercase tracking-widest"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={savingDeck || !selectedDeckForModal}
                      className="w-1/2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors text-xs uppercase tracking-widest flex items-center justify-center space-x-2"
                    >
                      {savingDeck ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                          <span>Salvando...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check"></i>
                          <span>Confirmar Deck</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
